import React, { useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperCore } from "swiper";
import "swiper/css";

type Props<T extends { uid?: string }> = {
  items: T[];
  index: number;                       // active index from your hook
  setIndex: (i: number) => void;
  initialSlide: number;
  remountKey: number;
  onNearStart: () => void;
  onNearEnd: () => void;
  initDoneRef: React.MutableRefObject<boolean>;
  suppressEdgeLoadsRef: React.MutableRefObject<boolean>;
  renderItem: (item: T, i: number) => React.ReactNode;
};

export function VideoSwiper<T extends { uid?: string }>({
  items, index, setIndex,
  initialSlide, remountKey,
  onNearStart, onNearEnd,
  initDoneRef, suppressEdgeLoadsRef,
  renderItem,
}: Props<T>) {
  const swiperRef = useRef<SwiperCore | null>(null);

   useEffect(() => {
    const s = swiperRef.current;
    if (!s) return;
    if (s.activeIndex !== index) s.slideTo(index, 0);
  }, [index, items.length]);

  return (
    <Swiper
      className="video-swiper"
      key={remountKey}                 // only when window is rebuilt
      direction="vertical"
      slidesPerView={1}
      initialSlide={initialSlide}
      nested
      touchEventsTarget="container"
      threshold={8}
      resistanceRatio={0}
      onSwiper={(s) => {
        swiperRef.current = s;
        // mark init done after Swiper is fully ready this tick
        setTimeout(() => (initDoneRef.current = true), 0);
      }}
      onSlideChange={(s) => {
        const i = s.activeIndex;
        setIndex(i);
        if (!initDoneRef.current || suppressEdgeLoadsRef.current) return;
        if (i >= items.length - 2) onNearEnd();
        if (i <= 1) onNearStart();
      }}
      onResize={() => {
        // keep the slide aligned after orientation/resize
        const s = swiperRef.current;
        if (!s) return;
        if (s.activeIndex !== index) s.slideTo(index, 0);
      }}
      style={{ height: "100vh", width: "100vw" }}
    >
      {/* Keep DOM identity stable: use the *video uid* as the key */}
      {items.map((it, i) => (
        <SwiperSlide key={it.uid!}>{renderItem(it, i)}</SwiperSlide>
      ))}
    </Swiper>
  );
}
